import LoginIcon from "@mui/icons-material/Login";
import Logout from "@mui/icons-material/Logout";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { Avatar, Box, Button, Chip, Divider, IconButton, Menu, Tooltip, Typography } from "@mui/material";
import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { LoginModal } from "./LoginModal";
import { useLogout } from "../../hooks/useLogout";
import { useAuthSelector } from "../../store/auth";
import useAuth from "../../hooks/useAuth";
import ErrorBoundary from "../common/ErrorBoundary";
import AlertDialog from "../alerts-dialog/AlertDialog";

const UserButton = () => {
  const { user: profile } = useAuthSelector();
  const { canManageUsers, canManageConfiguration, canAdminister, hasWritePermissions } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  // Open the admin "Settings" overlay (rendered by MainLayout on ?admin=true).
  const handleOpenSettings = () => {
    const params = new URLSearchParams(searchParams);
    params.set("admin", "true");
    setSearchParams(params);
  };

  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const { logout, isPending, isError } = useLogout();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onLogout = () => {
    handleClose();
  };

  const handleLoginClick = () => {
    setLoginModalOpen(true);
  };

  const userFullName = `${profile?.first_name || ""} ${profile?.last_name || ""}`.trim();

  const roleLabel = profile?.role ? profile.role.charAt(0).toUpperCase() + profile.role.slice(1).toLowerCase() : "";

  // Colour by what the person can do, not what their group is called. Keying
  // off the name meant a custom group with admin-equivalent permissions fell
  // through to the muted "viewer" badge. This was the last place in the client
  // that read a role name for anything.
  const roleColor = (() => {
    if (canAdminister) return { bg: "error.dark", text: "white" };
    if (hasWritePermissions) return { bg: "primary.dark", text: "white" };
    return { bg: "grey.100", text: "grey.600" };
  })();

  return (
    <Box>
      {profile ? (
        <Box style={{ paddingLeft: "12px" }}>
          <Tooltip title='User Settings'>
            <IconButton onClick={handleClick} style={{ height: "28px", width: "28px" }}>
              <Avatar src={undefined} alt={userFullName || "User"} />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            onClick={handleClose}
            slotProps={{
              paper: {
                sx: {
                  borderRadius: 4,
                  minWidth: 240,
                  boxShadow: "0px 8px 24px rgba(0,0,0,0.12)",
                  mt: "20px",
                  py: "4px"
                }
              }
            }}
          >
            <Box sx={{ px: 22, marginBottom: "12px" }}>
              <Box sx={{ display: "flex", gap: "8px", paddingRight: "38px" }}>
                <Avatar src={undefined} alt={userFullName || "User"} sx={{ height: "46px", width: "46px" }} />
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Typography variant='h6' sx={{ fontWeight: 700, color: "grey.700" }}>
                    {userFullName}
                  </Typography>
                  <Typography variant='body2' sx={{ textDecoration: "underline" }}>
                    {profile.email}
                  </Typography>
                  <Chip
                    label={roleLabel}
                    size='small'
                    sx={{
                      alignSelf: "flex-start",
                      height: 20,
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      bgcolor: roleColor.bg,
                      color: roleColor.text,
                      border: "none",
                      marginTop: "4px"
                    }}
                  />
                </Box>
              </Box>
            </Box>
            <Box sx={{ px: 22, py: 1.5 }}>
              <Typography variant='h6' sx={{ fontWeight: "400" }}>
                {profile?.organisation?.name || ""}
              </Typography>
            </Box>
            <Divider sx={{ my: "4px" }} />
            {/* Matches MainLayout's gate on the ?admin=true overlay — otherwise
                a config-only admin would have no way to open it. */}
            {(canManageConfiguration || canManageUsers) && <SettingsButton onOpen={handleOpenSettings} />}
            <ErrorBoundary
              onError={(err) => {
                setShowErrorAlert(true);
                setError(err);
              }}
            >
              <LogoutButton onLogout={onLogout} />
            </ErrorBoundary>
          </Menu>
        </Box>
      ) : (
        <>
          <Tooltip title='Login'>
            <IconButton onClick={handleLoginClick}>
              <LoginIcon />
            </IconButton>
          </Tooltip>
          <LoginModal open={loginModalOpen} onClose={() => setLoginModalOpen(false)} />
        </>
      )}
      <AlertDialog open={showErrorAlert} onClose={() => setShowErrorAlert(false)} variant='error' maxWidth='sm' fullWidth>
        <AlertDialog.Content sx={{ maxWidth: "32rem", mx: "auto" }}>
          <AlertDialog.Icon />
          <AlertDialog.Title>{error?.name}</AlertDialog.Title>
          <Typography variant='subtitle1' color='gray.500'>
            {error?.message}
          </Typography>
        </AlertDialog.Content>
        <AlertDialog.Actions>
          <Button variant='contained' onClick={() => setShowErrorAlert(false)} disableElevation disabled={isPending && !isError}>
            Close
          </Button>
          <Button variant='secondary' onClick={() => logout(true)} disabled={isPending}>
            Logout, anyway
          </Button>
        </AlertDialog.Actions>
      </AlertDialog>
    </Box>
  );
};

function SettingsButton({ onOpen }: { onOpen: () => void }) {
  return (
    <Box
      sx={{
        display: "flex",
        px: 22,
        py: 10,
        gap: "10px",
        alignItems: "center",
        cursor: "pointer",
        ":hover": { backgroundColor: "grey.200" }
      }}
      onClick={onOpen}
    >
      <SettingsOutlinedIcon fontSize='medium' />
      <Typography variant='body1'>Settings</Typography>
    </Box>
  );
}

function LogoutButton({ onLogout }: { onLogout: () => void }) {
  const { logout } = useLogout();

  const handleLogout = () => {
    logout();
    onLogout?.();
  };

  return (
    <Box
      sx={{
        display: "flex",
        px: 22,
        py: 10,
        gap: "10px",
        alignItems: "center",
        cursor: "pointer",
        ":hover": { backgroundColor: "grey.200" }
      }}
      onClick={handleLogout}
    >
      <Logout fontSize='medium' />
      <Typography variant='body1'>Logout</Typography>
    </Box>
  );
}

export default UserButton;
